import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Form } from "react-final-form";
import UiButton from "../button/Button";
import UiFieldMessage from "../field-message/FieldMessage.component";
import FormSection from "../form-section/FormSection.component";
import UiInputText from "../input-text/InputText.component";
import UiSelect from "../select/Select.component";
import { ListOption } from "../../models/misc.model";
import { Formatter } from "../../services/formatter/formatter.service";
import { EndpointsService } from "../../services/endpoints/endpoints.service";
import { AxiosError } from "axios";
import { mappedAxiosError } from "../../services/http/http.service";
import { useActiveProvidersInfo } from "../../hooks/useActiveProvidersInfo";
import {
	ModalAlertData,
	ModalAlertEntity,
	ModalAlertFormProps,
} from "../../models/components/modal-alert.model";
import { getAlertReason } from "../../utils/alert-details";
import { Task } from "../../models/entities/task.model";

const isTask = (entity: ModalAlertEntity): entity is Task => "participantId" in entity;

const ModalAlertForm = (props: ModalAlertFormProps) => {
	const { t } = useTranslation("common");
	const [formMessage, setFormMessage] = useState<ModalAlertData["formMessage"]>(undefined);

	const coaches = useActiveProvidersInfo();

	const entity = props.entity;
	// Shared with the Requires-attention card and table so the three surfaces can
	// never disagree about how an alert reason is labelled (KAN-2384).
	const reasonLabel = getAlertReason(t, entity?.reason);

	const currentAssignee = entity
		? isTask(entity)
			? ((entity.assignee as number | null) ?? null)
			: (entity.assignedTo ?? null)
		: null;

	const handleSubmit = async (data: ModalAlertData) => {
		setFormMessage(undefined);

		if (!entity) return;

		try {
			if (isTask(entity)) {
				const response = await EndpointsService.dataRetriever.updateTask({
					id: entity.id,
					body: {
						assignee: data.assignedTo as number,
						participantId: entity.participantId,
					},
				});
				props.onComplete(true, response as unknown as ModalAlertEntity);
			} else {
				const response = await EndpointsService.dataRetriever.updateAlert({
					body: {
						id: entity.id,
						entityId: entity.entityId,
						assignedTo: data.assignedTo as number,
					},
				});
				props.onComplete(true, response);
			}
		} catch (error) {
			setFormMessage(
				mappedAxiosError(error as AxiosError, "PARTICIPANTS.ALERTS.MODAL.MESSAGE_ERROR")
			);
		}
	};

	return (
		<Form
			onSubmit={handleSubmit}
			initialValues={{
				kannactId: (entity as { kannactId?: string } | null)?.kannactId ?? "",
				reason: (reasonLabel ?? "") as string,
				details: entity?.details ?? "",
				assignedTo: currentAssignee,
			}}
			render={({ handleSubmit, submitting, form }) => (
				<form onSubmit={handleSubmit}>
					<FormSection className="row">
						<div className="row">
							<div className="col-12 col-sm-6 col-md-6 col-lg-6">
								<UiInputText
									id="kannactId"
									label="PARTICIPANTS.ALERTS.MODAL.FIELDS.kannactId"
									name="kannactId"
									value={entity?.userState?.kannactId ?? ""}
									disabled={true}
								/>
							</div>
							<div className="col-12 col-sm-6 col-md-6 col-lg-6">
								<UiInputText
									id="reason"
									label="PARTICIPANTS.ALERTS.MODAL.FIELDS.reason"
									name="reason"
									value={reasonLabel ?? ""}
									disabled={true}
								/>
							</div>
						</div>
						<div className="col-12 col-sm-12 col-md-12 col-lg-12">
							<UiInputText
								id="details"
								label="PARTICIPANTS.ALERTS.MODAL.FIELDS.details"
								name="details"
								value={entity?.details ?? ""}
								disabled={true}
							/>
						</div>
						<div className="col-12 col-sm-12 col-md-12 col-lg-12">
							<UiSelect
								id="assignedTo"
								label={
									!form.getState().values?.assignedTo &&
									(coaches.isFetching || coaches.isLoading)
										? "UI_COMPONENTS.SELECT.LOADING"
										: "PARTICIPANTS.ALERTS.MODAL.FIELDS.assignedTo"
								}
								name="assignedTo"
								onChange={(e) => form.change("assignedTo", e.target.value)}
								options={
									coaches.data?.map(
										(item) =>
											new ListOption({
												id: item.coachId,
												label: Formatter.fullName(
													item.firstName,
													item.lastName
												),
											})
									) ?? []
								}
								disabled={submitting}
								filter={true}
							/>
						</div>
					</FormSection>

					{formMessage && (
						<div className="form-message">
							<UiFieldMessage {...formMessage}></UiFieldMessage>
						</div>
					)}

					<div className="action-buttons">
						<UiButton
							label="UI_COMPONENTS.BUTTONS.CANCEL"
							className="p-button-outlined p-button-rounded"
							type="button"
							onClick={props.onCancel}
							disabled={submitting}
						/>
						<UiButton
							label="UI_COMPONENTS.BUTTONS.CONFIRM"
							className="p-button-rounded"
							type="submit"
							loading={submitting}
							disabled={submitting}
						/>
					</div>
				</form>
			)}
		/>
	);
};

export default ModalAlertForm;
